"use client"

import { useState, useEffect, Suspense } from "react"
import { useSearchParams, usePathname } from "next/navigation"
import { useGoogleReCaptcha } from "@/lib/recaptcha-client"
import { motion } from "framer-motion"
import { Section } from "@/components/layout/Section"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { FileText, Send, Upload, Briefcase } from "lucide-react"
import Link from "next/link"
import { CTA } from "@/components/sections/CTA"
import { type Locale } from "@/lib/i18n"

const positionMap: Record<string, string> = {
  "Senior Frontend Developer": "senior-frontend-developer",
  "UI/UX Designer": "ui-ux-designer",
  "Mobile App Developer": "mobile-app-developer",
  "AI/ML Engineer": "ai-ml-engineer",
}

function CareerApplicationForm() {
  const pathname = usePathname()
  const currentLocale: Locale = pathname?.startsWith('/en/') ? 'en' : 'tr'
  const searchParams = useSearchParams()
  const positionParam = searchParams.get("position")
  const { executeRecaptcha } = useGoogleReCaptcha()

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    experience: "",
    linkedin: "",
    portfolio: "",
    coverLetter: "",
  })

  const [resume, setResume] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (positionParam) {
      const mappedPosition = positionMap[positionParam] || positionParam.toLowerCase().replace(/\s+/g, "-")
      setFormData(prev => ({ ...prev, position: mappedPosition }))
    }
  }, [positionParam])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)
    setSubmitSuccess(null)

    if (!resume) {
      setSubmitError("Lütfen CV/özgeçmiş dosyanızı yükleyin.")
      return
    }

    try {
      setSubmitting(true)
      
      // Get reCAPTCHA token
      let recaptchaToken = ""
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha("career_form")
      }

      const body = new FormData()

      Object.entries(formData).forEach(([key, value]) => {
        body.append(key, value)
      })

      body.append("resume", resume)
      body.append("recaptchaToken", recaptchaToken)

      const response = await fetch("/api/career/apply", {
        method: "POST",
        body,
      })

      const data = await response.json()

      if (!response.ok) {
        setSubmitError(data.error || "Başvurunuz gönderilemedi. Lütfen tekrar deneyin.")
        return
      }

      setSubmitSuccess(data.message || "Başvurunuz alındı! En kısa sürede size dönüş yapacağız.")

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        position: "",
        experience: "",
        linkedin: "",
        portfolio: "",
        coverLetter: "",
      })
      setResume(null)
    } catch (error) {
      setSubmitError("Başvurunuz gönderilemedi. Lütfen daha sonra tekrar deneyin.")
    } finally {
      setSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setResume(e.target.files[0])
    }
  }

  return (
    <>
      {/* Hero Section */}
      <Section className="relative overflow-hidden bg-gradient-to-br from-[rgb(3,74,166)] via-[rgb(3,74,166)] to-[rgb(2,40,89)] text-white pt-24 pb-16 sm:pt-32 sm:pb-24">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_70%)]" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex p-4 bg-white/10 backdrop-blur-sm rounded-2xl mb-8"
          >
            <Briefcase className="h-12 w-12 sm:h-16 sm:w-16" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 font-mono"
          >
            Kariyer Başvurusu
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto"
          >
            LAK VERSE ailesine katılmak için başvuru formunu doldurun. Yeteneklerinizi ve deneyimlerinizi paylaşın.
          </motion.p>
        </div>
      </Section>

      {/* Form Section */}
      <Section className="py-16 lg:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden"
          >
            <form onSubmit={handleSubmit} className="p-6 sm:p-8 lg:p-12">
              {/* Kişisel Bilgiler */}
              <div className="space-y-6 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-[rgb(3,74,166)]/10 rounded-lg">
                    <FileText className="h-5 w-5 text-[rgb(3,74,166)]" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 font-mono">Kişisel Bilgiler</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Ad Soyad *</label>
                    <Input
                      type="text"
                      name="name"
                      placeholder="Adınız ve Soyadınız"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="h-12 border-slate-300 focus:border-[rgb(3,74,166)] focus:ring-[rgb(3,74,166)]"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">E-posta Adresi *</label>
                    <Input
                      type="email"
                      name="email"
                      placeholder="ornek@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="h-12 border-slate-300 focus:border-[rgb(3,74,166)] focus:ring-[rgb(3,74,166)]"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Telefon Numarası *</label>
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="05XX XXX XX XX"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="h-12 border-slate-300 focus:border-[rgb(3,74,166)] focus:ring-[rgb(3,74,166)]"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Başvurduğunuz Pozisyon *</label>
                    <select
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      required
                      className="flex h-12 w-full rounded-md border border-slate-300 bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(3,74,166)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Pozisyon Seçiniz</option>
                      <option value="senior-frontend-developer">Senior Frontend Developer</option>
                      <option value="ui-ux-designer">UI/UX Designer</option>
                      <option value="mobile-app-developer">Mobile App Developer</option>
                      <option value="ai-ml-engineer">AI/ML Engineer</option>
                      <option value="backend-developer">Backend Developer</option>
                      <option value="fullstack-developer">Full Stack Developer</option>
                      <option value="genel">Genel Başvuru</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Profesyonel Bilgiler */}
              <div className="space-y-6 pt-8 border-t border-slate-200 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-[rgb(3,74,166)]/10 rounded-lg">
                    <Briefcase className="h-5 w-5 text-[rgb(3,74,166)]" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 font-mono">Profesyonel Bilgiler</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Deneyim Süresi</label>
                    <select
                      name="experience"
                      value={formData.experience}
                      onChange={handleChange}
                      className="flex h-12 w-full rounded-md border border-slate-300 bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(3,74,166)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Deneyim Süresi Seçiniz</option>
                      <option value="0-1">0-1 Yıl</option>
                      <option value="1-3">1-3 Yıl</option>
                      <option value="3-5">3-5 Yıl</option>
                      <option value="5-7">5-7 Yıl</option>
                      <option value="7+">7+ Yıl</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">LinkedIn Profili</label>
                    <Input
                      type="url"
                      name="linkedin"
                      placeholder="https://linkedin.com/in/kullanici-adi"
                      value={formData.linkedin}
                      onChange={handleChange}
                      className="h-12 border-slate-300 focus:border-[rgb(3,74,166)] focus:ring-[rgb(3,74,166)]"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-sm font-medium text-slate-700">Portfolio / GitHub</label>
                    <Input
                      type="url"
                      name="portfolio"
                      placeholder="https://portfolio.com veya https://github.com/kullanici-adi"
                      value={formData.portfolio}
                      onChange={handleChange}
                      className="h-12 border-slate-300 focus:border-[rgb(3,74,166)] focus:ring-[rgb(3,74,166)]"
                    />
                  </div>
                </div>
              </div>

              {/* Özgeçmiş */}
              <div className="space-y-6 pt-8 border-t border-slate-200 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-[rgb(3,74,166)]/10 rounded-lg">
                    <Upload className="h-5 w-5 text-[rgb(3,74,166)]" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 font-mono">Özgeçmiş</h2>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">CV / Özgeçmiş Yükle *</label>
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-[rgb(3,74,166)] transition-colors">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      className="hidden"
                      id="resume-upload"
                      required
                    />
                    <label
                      htmlFor="resume-upload"
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <Upload className="h-8 w-8 text-slate-400" />
                      <span className="text-sm text-slate-600">
                        {resume ? resume.name : "PDF, DOC veya DOCX formatında dosya yükleyin"}
                      </span>
                      <span className="text-xs text-slate-500">Maksimum 5MB</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Ön Yazı */}
              <div className="space-y-6 pt-8 border-t border-slate-200 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-[rgb(3,74,166)]/10 rounded-lg">
                    <FileText className="h-5 w-5 text-[rgb(3,74,166)]" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 font-mono">Ön Yazı</h2>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Neden LAK VERSE? *</label>
                  <Textarea
                    name="coverLetter"
                    placeholder="Kendinizden bahsedin, neden bu pozisyona uygun olduğunuzu ve neden LAK VERSE'de çalışmak istediğinizi açıklayın..."
                    value={formData.coverLetter}
                    onChange={handleChange}
                    required
                    rows={8}
                    className="min-h-[200px] border-slate-300 focus:border-[rgb(3,74,166)] focus:ring-[rgb(3,74,166)] resize-none"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-6 border-t border-slate-200 flex flex-col sm:flex-row gap-4 justify-between items-center">
                <Link
                  href="/kariyer"
                  className="text-sm text-slate-600 hover:text-[rgb(3,74,166)] transition-colors"
                >
                  ← Kariyer sayfasına dön
                </Link>
                <Button
                  type="submit"
                  size="lg"
                  disabled={submitting}
                  className="w-full sm:w-auto bg-[rgb(3,74,166)] hover:bg-[rgb(3,62,140)] text-white px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Send className="mr-2 h-5 w-5" />
                  {submitting ? "Gönderiliyor..." : "Başvuruyu Gönder"}
                </Button>
              </div>

              {submitError && (
                <p className="mt-4 text-sm text-red-600">
                  {submitError}
                </p>
              )}
              {submitSuccess && (
                <p className="mt-4 text-sm text-green-600">
                  {submitSuccess}
                </p>
              )}
            </form>
          </motion.div>
        </div>
      </Section>
      <CTA locale={currentLocale} />
    </>
  )
}

export default function CareerApplicationPage() {
  return (
    <Suspense fallback={
      <Section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-slate-500">Yükleniyor...</div>
        </div>
      </Section>
    }>
      <CareerApplicationForm />
    </Suspense>
  )
}

